import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import {
  GATE_ORDER_PIPE,
  gateMeta,
  gateStatusShort,
  type GateKey,
} from '../../lib/employerVerificationCopy';
import type {
  EmployerVerificationListItem,
  PersistedGateCheckpointDto,
} from '../../api/employerVerificationDashboardApi';
import { resolvedGateDisplayStatus } from '../../lib/employerVerificationGateAudit';

function tierColor(theme: Theme, raw: string) {
  const u = raw.trim().toUpperCase();
  // Same paper background on every gate tile — status via text color only (Loan review pipeline pattern).
  const bg = theme.palette.background.paper;
  if (u === 'PASS') {
    return { bg, fg: theme.palette.success.dark };
  }
  if (u === 'FAIL') {
    return { bg, fg: theme.palette.error.dark };
  }
  if (u === 'REVIEW') {
    return { bg, fg: theme.palette.warning.dark };
  }
  return { bg, fg: theme.palette.text.secondary };
}

export type PipelineDensity = 'compact' | 'comfortable';

type Props = {
  gates: EmployerVerificationListItem['gates'];
  gateCheckpointAudit?: Record<string, PersistedGateCheckpointDto[]> | null;
  compact?: boolean;
  density?: PipelineDensity;
  pinFirstFailureKey?: GateKey | null;
};

export function GatePipelineStrip({
  gates,
  gateCheckpointAudit = null,
  compact,
  density = 'comfortable',
  pinFirstFailureKey,
}: Props) {
  const resolvedDensity: PipelineDensity = compact === true ? 'compact' : density;
  const theme = useTheme();
  const isCompact = resolvedDensity === 'compact';

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="stretch"
      gap={isCompact ? 0.5 : 0.85}
      sx={{
        ...(isCompact ? { maxWidth: 560 } : {}),
      }}
      role="list"
      aria-label="Verification checkpoints, in pipeline order"
    >
      {GATE_ORDER_PIPE.map((gk, idx) => {
        const raw = (gates[gk] ?? '').trim();
        const resolved = resolvedGateDisplayStatus(raw, gateCheckpointAudit?.[gk]);
        const st = gateStatusShort(resolved || null);
        const meta = gateMeta[gk];
        const { bg, fg } = tierColor(theme, resolved || 'NOT_RUN');
        const isPin =
          !!pinFirstFailureKey &&
          pinFirstFailureKey === gk &&
          resolved === 'FAIL';

        return (
          <Stack key={gk} direction="row" alignItems="center" sx={{ mb: isCompact ? 0.25 : 0 }} role="listitem">
            {idx > 0 ? (
              <EastRoundedIcon
                sx={{
                  fontSize: isCompact ? 16 : 18,
                  mx: isCompact ? 0.3 : 0.4,
                  color: alpha(theme.palette.text.secondary, 0.55),
                }}
                aria-hidden
              />
            ) : null}
            <Box
              sx={{
                px: isCompact ? 0.85 : 1.1,
                py: isCompact ? 0.45 : 0.65,
                borderRadius: 1.25,
                bgcolor: bg,
                border: '1px solid',
                borderColor: isPin ? alpha(theme.palette.error.main, 0.65) : alpha(theme.palette.divider, 0.65),
                minWidth: isCompact ? 76 : 88,
                ...(isPin
                  ? {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.error.main, 0.35)}`,
                    }
                  : {}),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: fg,
                  letterSpacing: 0.02,
                  display: 'block',
                  lineHeight: 1.25,
                  fontSize: isCompact ? '0.74rem' : '0.8rem',
                }}
              >
                {meta.short} · {st}
              </Typography>
              {!isCompact ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontSize: '0.72rem', mt: 0.25, lineHeight: 1.35 }}
                >
                  {meta.title}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
}

export function findFirstFailingGateKey(gates: Record<string, string | null>): GateKey | null {
  for (const key of GATE_ORDER_PIPE) {
    const u = (gates[key] ?? '').trim().toUpperCase();
    if (u === 'FAIL') return key;
  }
  return null;
}
